import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { Fragment, ReactNode } from "react";
import { cn } from "utils/ui";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface Props {
  children?: ReactNode;
  isShown: boolean;
  onClose?: () => void;
  showCloseButton?: boolean;
  maxWidthClassName?: string;
}

export default function Modal({
  isShown,
  onClose = () => {},
  showCloseButton = false,
  children,
  maxWidthClassName = "sm:max-w-lg",
}: Props) {
  return (
    <Transition show={isShown} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-10 overflow-y-auto" onClose={onClose}>
        <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity dark:bg-gray-700/75" />
          </TransitionChild>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
            &#8203;
          </span>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              className={cn(
                "relative inline-block rounded-lg bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all dark:bg-slate-800 sm:my-8 sm:w-full sm:p-6 sm:align-middle",
                maxWidthClassName
              )}
            >
              {showCloseButton && (
                <button
                  type="button"
                  className="absolute right-3 top-3 z-50 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:ring-offset-2 dark:text-gray-400 dark:hover:bg-slate-700 dark:hover:text-gray-300 dark:focus:ring-slate-600 sm:right-4 sm:top-4"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="size-5" aria-hidden="true" />
                </button>
              )}
              {children}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
